/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withSitecoreContext, Placeholder, } from '@sitecore-jss/sitecore-jss-react';
import get from 'lodash/get';
import map from 'lodash/map';
import compose from 'lodash/fp/compose';
import defaultsDeep from 'lodash/defaultsDeep';
import { withNamespaces } from 'react-i18next';
import throttle from 'lodash/throttle';
import { Container, Row, Column, MediaQueries } from '../../../styles/Grid';
import { Icon } from '../../ui/Icon';
import DotComponent from '../../ui/DotComponent';
import { showElementCss, hideElementCss } from '../../../utils/responsive';
import { Breakpoints, VIEWPORTS } from '../../../styles/Grid/GridSizes';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  buildImagePath,
  parseUrlConfigParams,
} from '../../../utils/cloudinary';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import {
  footerCss,
  topFooterSectionCss,
  breadcrumbsContainerCss,
  breadcrumbCss,
  breadcrumbItemCss,
  downloadHeadlineCss,
  downloadSectionCss,
  logoLinkCss,
  logoImageCss,
  breadcrumbChevronCss,
  storeLogoCss,
  storeLogosContainerCss,
  storeLinkCss,
  breadcrumbsScrollContainerCss,
  breadcrumbsShadowContainerCss,
  displayLeftShadowCss,
  displayRightShadowCss,
  categoriesContainerCss,
  categoryContainerCss,
  categoryButtonCss,
  categoryChevronCss,
  crevronExpandedCss,
  categoryContentCss,
  categoryLinkContainerCss,
  categoryLinkCss,
  categoryButtonExpandedCss,
  categoryContentExpandedCss,
  categoryHeadlineCss,
  footerLowerSectionCss,
  breadcrumbLinkCss,
} from './index.styles';
import { withConsumer } from '../../../contexts';
import { StickyBarConsumer } from '../../../contexts/StickyBarContext';

const stickyBarSelector = '[data-sticky-bottom-bar]';

const mainLogoSizeConfig = parseUrlConfigParams({
  ...CLOUDINARY_BASE_VALUES,
  gravity: 'auto:subject',
  height: 45,
  width: 88,
}).config;

const downloadSizeConfig = parseUrlConfigParams({
  ...CLOUDINARY_BASE_VALUES,
  gravity: 'auto:subject',
  height: 40,
  width: 121,
}).config;

const defaultData = {
  Breadcrumb: { value: [] },
  Categories: [],
  DownloadHeadline: { value: '' },
  GeneralLinks: { value: [] },
  LanguageSelectorCta: {
    value: {
      href: '',
      text: '',
    },
  },
  LanguageSelector: {
    value: {
      name: '',
      code: '',
      flag: '',
    },
  },
  Logo: { value: '' },
  Logos: { value: [] },
  MainCategoriesInColumn: { value: '' },
  RegusLogo: {
    value: {
      src: '',
      alt: '',
    },
  },
  SocialMedia: { value: [] },
  SubCategoriesInLine: { value: '' },
};

const getCategoryKey = categoryIndex => `category-${categoryIndex}`;

export class FooterCategoryLinks extends SafePureComponent {
  breadcrumbsScrollContainer = React.createRef();
  fields = defaultsDeep(get(this, 'props.fields'), defaultData);

  state = {
    displayLeftShadow: false,
    displayRightShadow: false,
    accordionStates: {},
    jsonFields: {},
    stickyBarOffset: 0,
  };

  componentDidMount() {
    if (this.breadcrumbsScrollContainer.current) {
      this.breadcrumbsScrollContainer.current.addEventListener(
        'scroll',
        this.breadcrumbsScrollHandler,
      );
      this.breadcrumbsScrollHandler();
    }

    this.updateStickyBarOffset();
    window.addEventListener('resize', this.updateStickyBarOffset);
  }

  componentWillUnmount() {
    this.breadcrumbsScrollContainer.current.removeEventListener(
      'scroll',
      this.breadcrumbsScrollHandler,
    );
    window.removeEventListener('resize', this.updateStickyBarOffset);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.stickyBarContext.initialized !==
        get(this.props, 'stickyBarContext.initialized') &&
      get(this.props, 'stickyBarContext.initialized')
    ) {
      this.updateStickyBarOffset();
    }
  }

  updateStickyBarOffset = throttle(() => {
    const stickyBar = document.querySelector(stickyBarSelector);
    if (stickyBar) {
      const isDesktop = window.innerWidth >= Breakpoints[VIEWPORTS.lg];
      const height = isDesktop ? 0 : stickyBar.clientHeight;
      this.setState({ stickyBarOffset: height });
    }
  }, 80);

  breadcrumbsScrollHandler = () => {
    const target = this.breadcrumbsScrollContainer.current;
    const scrollLeft = target.scrollLeft;
    const shouldDisplayLeftShadow = scrollLeft > 0;
    const shouldDisplayRightShadow =
      target.scrollLeft + target.clientWidth < target.scrollWidth;

    this.setState({
      displayLeftShadow: shouldDisplayLeftShadow,
      displayRightShadow: shouldDisplayRightShadow,
    });
  };

  renderBreadcrumbs() {
    const { displayLeftShadow, displayRightShadow } = this.state;
    const { Breadcrumb } = this.fields;
    const breadcrumbValues = get(Breadcrumb, 'value', []);
    const { t } = this.props;

    if (breadcrumbValues.length === 0) {
      return;
    }

    return (
      <div
        css={[
          breadcrumbsShadowContainerCss,
          displayLeftShadow && displayLeftShadowCss,
          displayRightShadow && displayRightShadowCss,
        ]}
      >
        <div
          ref={this.breadcrumbsScrollContainer}
          css={breadcrumbsScrollContainerCss}
        >
          <div css={breadcrumbsContainerCss} data-testid="breadcrumbs">
            {breadcrumbValues.map(({ text, href }, index) => (
              <React.Fragment key={`breadcrumb-${index}`}>
                <div css={breadcrumbCss}>
                  {index + 1 === breadcrumbValues.length ? (
                    <span css={breadcrumbItemCss}>{t(text)}</span>
                  ) : (
                    <a css={breadcrumbLinkCss} href={href} aria-label={t(text)}>
                      {t(text)}
                    </a>
                  )}
                </div>
                {index + 1 < breadcrumbValues.length && (
                  <Icon customCss={breadcrumbChevronCss} name="chevron-right" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  toggleAccordionState = accordionName => {
    const { accordionStates } = this.state;

    this.setState({
      accordionStates: {
        ...accordionStates,
        [accordionName]: !accordionStates[accordionName],
      },
    });
  };

  renderCategories() {
    
  }

  renderDownloadSection() {
    const { fields } = this;
    const { Logos } = fields;

    return (
      <div css={downloadSectionCss}>
        <h2 css={downloadHeadlineCss}>
          {get(fields, 'DownloadHeadline.value')}
          <DotComponent />
        </h2>
        <div css={storeLogosContainerCss}>
          {get(Logos, 'value', []).map((logo, key) => (
            <a
              css={storeLinkCss}
              href={get(logo, 'link.value.href')}
              key={`download-logo-${key}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img
                alt={
                  get(logo, 'image.value.alt') || get(logo, 'link.value.text')
                }
                css={storeLogoCss}
                src={buildImagePath(
                  downloadSizeConfig,
                  get(logo, 'image.value.src'),
                )}
              />
            </a>
          ))}
        </div>
      </div>
    );
  }

  /**
   * Creates an object in relation to the argument's value,
   * setting the target an rel props if true
   * @param {bool} isAnExternalLink
   * @returns {Object}
   */
  getExternalLinkAttributes(isAnExternalLink) {
    return isAnExternalLink
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};
  }

  safeRender() {
    const { Categories } = this.fields;
    const { accordionStates } = this.state;

    return (
      <div css={categoriesContainerCss}>
        <Row>
          {map(Categories, (category, index) => {
            const categoryKey = getCategoryKey(index);

            return (
              <Column key={categoryKey} cols={{ sm: 6, md: [2, 6] }}>
                <div css={categoryContainerCss}>
                  <button
                    css={[
                      categoryButtonCss,
                      accordionStates[categoryKey] && categoryButtonExpandedCss,
                      hideElementCss(MediaQueries.mdAndUp),
                    ]}
                    aria-label="toggle accordion"
                    onClick={() => this.toggleAccordionState(categoryKey)}
                  >
                    <span>{category.headline}</span>
                    <Icon
                      name="chevron-down"
                      css={[
                        categoryChevronCss,
                        accordionStates[categoryKey] && crevronExpandedCss,
                      ]}
                    />
                  </button>
                  <h4
                    css={[
                      categoryHeadlineCss,
                      showElementCss(MediaQueries.mdAndUp),
                    ]}
                  >
                    {category.headline}
                  </h4>
                  <div
                    css={[
                      categoryContentCss,
                      accordionStates[categoryKey] &&
                        categoryContentExpandedCss,
                    ]}
                  >
                    {category.links.map((link, linkIndex) => {
                      const isAnExternalLink = link.target === '_blank';
                      return (
                        <div
                          key={`category-link-${linkIndex}`}
                          css={categoryLinkContainerCss}
                        >
                          <a
                            {...this.getExternalLinkAttributes(
                              isAnExternalLink,
                            )}
                            css={categoryLinkCss}
                            href={link.href}
                          >
                            {link.text}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Column>
            );
          })}
        </Row>
      </div>
    );
}
}

FooterCategoryLinks.displayName = 'FooterCategoryLinks';

const stringType = {
  value: PropTypes.string,
};

const arrayType = {
  value: PropTypes.array,
};

FooterCategoryLinks.defaultProps = {
  fields: PropTypes.shape({
    Breadcrumb: arrayType,
    Categories: PropTypes.array,
    DownloadHeadline: stringType,
    GeneralLinks: arrayType,
    LanguageSelectorCta: PropTypes.shape({
      value: PropTypes.shape({
        href: PropTypes.string,
        text: PropTypes.string,
      }),
    }),
    LanguageSelector: PropTypes.shape({
      value: PropTypes.shape({
        name: PropTypes.string,
        code: PropTypes.string,
        flag: PropTypes.string,
      }),
    }),
    Logo: stringType,
    Logos: arrayType,
    MainCategoriesInColumn: stringType,
    RegusLogo: PropTypes.shape({
      value: PropTypes.shape({
        alt: PropTypes.string,
        height: PropTypes.string,
        src: PropTypes.string,
        width: PropTypes.string,
      }),
    }),
    SocialMedia: arrayType,
    SubCategoriesInLine: stringType,
  }),
  t: key => key,
};

export default compose(
  withNamespaces(),
  withSitecoreContext(),
  withConsumer(StickyBarConsumer, 'stickyBarContext'),
)(FooterCategoryLinks);
